import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { clearConversation, selectUnreadEntries } from '../../redux/slices/chatUnread';

interface Props {
  onItemSelected: () => void;
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

export default function ChatNotificationsPanel({ onItemSelected }: Props) {
  const entries = useSelector(selectUnreadEntries);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  if (entries.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--gt-text-muted, #6b7280)' }}>
        You're all caught up.
      </div>
    );
  }

  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {entries.map((e) => (
        <li
          key={e.conversationId}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '0 6px 0 0',
            borderBottom: '1px solid var(--gt-divider, rgba(0,0,0,.04))',
          }}
        >
          <button
            type="button"
            onClick={() => {
              navigate(`/chat?open=${encodeURIComponent(e.conversationId)}`);
              dispatch(clearConversation(e.conversationId));
              onItemSelected();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flex: 1,
              minWidth: 0,
              padding: '10px 8px 10px 14px',
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'var(--gt-accent-soft, #e5f3ec)',
                color: 'var(--gt-accent, #1f4d3f)',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                flexShrink: 0,
              }}
            >
              {e.name.slice(0, 2).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {e.name}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--gt-text-muted, #6b7280)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={e.lastContent ?? undefined}
              >
                {e.lastContent
                  ? (e.count > 1
                      ? `${e.lastContent} · +${e.count - 1} more`
                      : e.lastContent)
                  : `${e.count} new message${e.count === 1 ? '' : 's'}`}
              </div>
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--gt-text-muted, #9ca3af)',
                flexShrink: 0,
              }}
            >
              {relativeTime(e.lastActivity)}
            </div>
          </button>
          <button
            type="button"
            aria-label="Mark as read"
            title="Mark as read"
            onClick={(ev) => {
              ev.stopPropagation();
              dispatch(clearConversation(e.conversationId));
            }}
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              border: '1px solid var(--gt-divider, rgba(0,0,0,.08))',
              background: 'var(--gt-surface, #fff)',
              color: 'var(--gt-accent, #1f4d3f)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Check style={{ width: 14, height: 14 }} />
          </button>
        </li>
      ))}
    </ul>
  );
}
